import { Injectable, Injector } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ErrorLogService } from '../services/error-log.service';

/**
 * HTTP interceptor that reports HTTP error responses (status >= 400)
 * to the backend error log for permanent storage.
 *
 * Excludes:
 * - The /error-log endpoint itself (to prevent infinite loops)
 * - 401 errors (handled by UnathorizedInterceptor)
 *
 * Reporting is fire-and-forget and never blocks the original error flow.
 */
@Injectable()
export class ErrorLoggingInterceptor implements HttpInterceptor {
  constructor(private injector: Injector) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      tap({
        error: (err: any) => {
          if (!(err instanceof HttpErrorResponse)) {
            return;
          }

          // Skip reporting errors from the error-log endpoint itself
          if (request.url.includes('/error-log')) {
            return;
          }

          // Skip 401 — handled by UnathorizedInterceptor (session redirect)
          if (err.status === 401) {
            return;
          }

          // Skip expected business errors (codes 1000-1099, the same range
          // the backend error handler skips in alineapi/app.js). These are
          // normal app flow: NoDataFound, MandatoryFields, ObjectAlreadyExists,
          // MultipleMainDiagnoses, cane capacity, straw number in use, etc.
          const errorBody = err.error;
          if (errorBody && typeof errorBody === 'object') {
            const code = errorBody.code;
            if (code >= 1000 && code <= 1099) {
              return;
            }
          }

          try {
            const errorLogService = this.injector.get(ErrorLogService);
            let currentUrl = '';
            try {
              const router = this.injector.get(Router);
              currentUrl = router.url;
            } catch {
              currentUrl = window.location.pathname;
            }

            let errorCode: number | undefined;
            let errorName: string | undefined;
            let errorMessage: string | undefined;

            if (errorBody && typeof errorBody === 'object') {
              errorCode =
                typeof errorBody.code === 'number' ? errorBody.code : undefined;
              errorName = errorBody.name;
              errorMessage = errorBody.message || err.message;
            } else {
              errorMessage = err.message;
            }

            errorLogService.reportError({
              errorCode,
              errorName: errorName || `HttpError_${err.status}`,
              errorMessage: (errorMessage || '').substring(0, 5000),
              httpStatus: err.status,
              apiUrl: request.url,
              httpMethod: request.method,
              uiUrl: currentUrl,
              additionalInfo: {
                type: 'http_error',
                statusText: err.statusText,
                userAgent: navigator.userAgent
              }
            });
          } catch (reportErr) {
            console.error(
              'ErrorLoggingInterceptor: failed to report error:',
              reportErr
            );
          }
        }
      })
    );
  }
}
